import React, { useState, useEffect } from 'react';
import api from '../api';
import { format } from 'date-fns';

const ManageRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [year, setYear] = useState(new Date().getFullYear());
  const [declineModal, setDeclineModal] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [amendModal, setAmendModal] = useState(null);
  const [amendForm, setAmendForm] = useState({ absence_type: 'holiday', start_date: '', end_date: '', notes: '', half_day: false, half_day_period: 'am' });
  const [amendSaving, setAmendSaving] = useState(false);
  const [logModal, setLogModal] = useState(false);
  const [logForm, setLogForm] = useState({ user_id: '', absence_type: 'holiday', start_date: '', end_date: '', notes: '', half_day: false, half_day_period: 'am' });
  const [logSaving, setLogSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const typeLabels = { holiday: 'Holiday', sick: 'Sick Day', awol: 'Absent Without Leave' };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year });
      // Cancellations tab lists all APPROVED holidays so an admin can cancel any of them.
      if (filter === 'cancellations') params.append('status', 'approved');
      else if (filter !== 'all') params.append('status', filter);
      const res = await api.get(`/api/holidays/all?${params}`);
      setRequests(res.data);
    } catch (err) {
      setError('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter, year]);

  useEffect(() => {
    api.get('/api/users').then(r => setStaffList(r.data.filter(u => u.active))).catch(() => {});
  }, []);

  const openLog = () => {
    setLogForm({ user_id: '', absence_type: 'holiday', start_date: '', end_date: '', notes: '', half_day: false, half_day_period: 'am' });
    setLogModal(true);
    setError('');
  };

  const saveLog = async () => {
    if (logSaving) return;
    if (!logForm.user_id) return setError('Please choose a staff member');
    if (!logForm.start_date) return setError('Please select a date');
    if (!logForm.half_day && !logForm.end_date) return setError('Please select start and last day');
    setLogSaving(true);
    setError('');
    try {
      const payload = logForm.half_day ? { ...logForm, end_date: logForm.start_date } : logForm;
      const res = await api.post('/api/holidays/log', payload);
      const staffName = res.data.staff_name || 'Staff member';
      const remainingNote = res.data.remaining !== null && res.data.remaining !== undefined
        ? ` They now have ${res.data.remaining} days remaining this year.`
        : '';
      setSuccess(`Logged ${res.data.days_taken} day${res.data.days_taken !== 1 ? 's' : ''} for ${staffName}.${remainingNote}`);
      setLogModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not log the absence.');
    } finally {
      setLogSaving(false);
    }
  };

  const openAmend = (r) => {
    setAmendModal(r);
    setAmendForm({
      absence_type: r.absence_type,
      start_date: r.start_date.substring(0, 10),
      end_date: r.end_date.substring(0, 10),
      notes: r.notes || '',
      half_day: !!r.half_day,
      half_day_period: r.half_day_period || 'am'
    });
    setError('');
  };

  const saveAmend = async () => {
    if (amendSaving) return;
    setAmendSaving(true);
    setError('');
    try {
      const payload = amendForm.half_day ? { ...amendForm, end_date: amendForm.start_date } : amendForm;
      await api.patch(`/api/holidays/${amendModal.id}`, payload);
      setSuccess(`Booking updated for ${amendModal.staff_name} — they have been emailed the corrected details.`);
      setAmendModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update the booking.');
    } finally {
      setAmendSaving(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.patch(`/api/holidays/${id}/status`, { status: 'approved' });
      setSuccess('Request approved');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve');
    }
  };

  const handleCancellation = async (id, decision) => {
    try {
      await api.patch(`/api/holidays/${id}/cancellation`, { decision });
      setSuccess(decision === 'approve' ? 'Holiday cancelled — days returned to their allowance' : 'Cancellation declined — holiday stands');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to action cancellation');
    }
  };

  const handleDirectCancel = async (id) => {
    if (!window.confirm('Cancel this holiday? The days will go back onto the employee\'s allowance and they will be emailed.')) return;
    try {
      await api.post(`/api/holidays/${id}/cancel`);
      setSuccess('Holiday cancelled — days returned to their allowance');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel holiday');
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) return setError('Please provide a reason for declining');
    try {
      await api.patch(`/api/holidays/${declineModal}/status`, { status: 'declined', decline_reason: declineReason });
      setSuccess('Request declined');
      setDeclineModal(null);
      setDeclineReason('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to decline');
    }
  };

  const years = [new Date().getFullYear() - 1, new Date().getFullYear()];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Manage Requests</h1>
          <p className="page-subtitle">Review and action staff absence requests</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-gold" onClick={openLog}>+ Log Absence</button>
          <select className="form-control" style={{ width: 'auto' }} value={year} onChange={e => setYear(Number(e.target.value))}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error" onClick={() => setError('')}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess('')}>{success}</div>}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '6px', padding: '4px', width: 'fit-content' }}>
        {['pending', 'approved', 'declined', 'cancellations', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '8px 18px', border: 'none', borderRadius: '4px', cursor: 'pointer',
            fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: '500', letterSpacing: '0.5px',
            textTransform: 'capitalize',
            background: filter === f ? 'var(--navy)' : 'transparent',
            color: filter === f ? 'var(--white)' : 'var(--grey-mid)',
            transition: 'all 0.15s'
          }}>
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div className="loader">Loading...</div>
        ) : requests.length === 0 ? (
          <div className="empty-state">
            <h3>No {filter !== 'all' ? filter : ''} requests</h3>
            <p>Nothing to show for the selected filters</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Staff Member</th>
                  <th>Type</th>
                  <th>Dates</th>
                  <th>Days</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight: '500' }}>{r.staff_name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--grey-mid)' }}>{r.job_title || r.department || ''}</div>
                    </td>
                    <td><span className={`badge badge-${r.absence_type}`}>{typeLabels[r.absence_type]}</span></td>
                    <td style={{ fontSize: '13px' }}>
                      {format(new Date(r.start_date.substring(0,10) + 'T00:00:00'), 'd MMM yy')}
                      {r.start_date !== r.end_date && <> &mdash; {format(new Date(r.end_date.substring(0,10) + 'T00:00:00'), 'd MMM yy')}</>}
                    </td>
                    <td>{r.days_taken}{r.half_day ? ` (½ ${r.half_day_period === 'pm' ? 'PM' : 'AM'})` : ''}</td>
                    <td style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>{format(new Date(r.created_at), 'd MMM')}</td>
                    <td>
                      <span className={`badge badge-${r.status}`}>{r.status.charAt(0).toUpperCase() + r.status.slice(1)}</span>
                      {r.status === 'declined' && r.decline_reason && (
                        <div style={{ fontSize: '11px', color: 'var(--danger)', marginTop: '3px' }}>{r.decline_reason}</div>
                      )}
                      {r.status === 'approved' && r.cancellation_requested && (
                        <div style={{ fontSize: '11px', color: 'var(--danger)', marginTop: '3px', fontWeight: 600 }}>
                          Cancellation requested{r.cancellation_reason ? `: ${r.cancellation_reason}` : ''}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--grey-mid)', maxWidth: '150px' }}>{r.notes || '-'}</td>
                    <td>
                      {r.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button className="btn btn-success btn-sm" onClick={() => handleApprove(r.id)}>Approve</button>
                          <button className="btn btn-danger btn-sm" onClick={() => { setDeclineModal(r.id); setDeclineReason(''); }}>Decline</button>
                        </div>
                      )}
                      {r.status === 'approved' && r.cancellation_requested && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button className="btn btn-success btn-sm" onClick={() => handleCancellation(r.id, 'approve')}>Approve cancellation</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleCancellation(r.id, 'decline')}>Decline</button>
                        </div>
                      )}
                      {filter === 'cancellations' && r.status === 'approved' && !r.cancellation_requested && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDirectCancel(r.id)}>Cancel holiday</button>
                      )}
                      {['pending', 'approved'].includes(r.status) && (
                        <button className="btn btn-outline btn-sm" style={{ marginTop: 4 }} onClick={() => openAmend(r)}>Amend</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Log absence modal — book on a staff member's behalf, past dates allowed */}
      {logModal && (
        <div className="modal-overlay" onClick={() => setLogModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Log Absence</h3>
              <button className="modal-close" onClick={() => setLogModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--grey-mid)', marginBottom: 16 }}>
                Record an absence on a staff member's behalf — it goes straight in as approved.
                Past dates are allowed, so use this to add holiday already taken this year.
              </p>
              <div className="form-group">
                <label className="form-label">Staff Member</label>
                <select className="form-control" value={logForm.user_id} onChange={e => setLogForm({ ...logForm, user_id: e.target.value })}>
                  <option value="">Choose...</option>
                  {staffList.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-control" value={logForm.absence_type} onChange={e => setLogForm({ ...logForm, absence_type: e.target.value })}>
                  <option value="holiday">Holiday</option>
                  <option value="sick">Sick Day</option>
                  <option value="awol">Absent Without Leave</option>
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={logForm.half_day} onChange={e => setLogForm({ ...logForm, half_day: e.target.checked })} style={{ width: 16, height: 16, accentColor: 'var(--gold)' }} />
                  <span style={{ fontSize: 14, color: 'var(--navy)' }}>Half day</span>
                </label>
              </div>
              {logForm.half_day ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label className="form-label">Date</label>
                    <input type="date" className="form-control" value={logForm.start_date} onChange={e => setLogForm({ ...logForm, start_date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">AM or PM</label>
                    <select className="form-control" value={logForm.half_day_period} onChange={e => setLogForm({ ...logForm, half_day_period: e.target.value })}>
                      <option value="am">Morning (AM)</option>
                      <option value="pm">Afternoon (PM)</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label className="form-label">Start Date</label>
                    <input type="date" className="form-control" value={logForm.start_date} onChange={e => setLogForm({ ...logForm, start_date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Day Off</label>
                    <input type="date" className="form-control" value={logForm.end_date} onChange={e => setLogForm({ ...logForm, end_date: e.target.value })} />
                    <p style={{ fontSize: 11, color: 'var(--grey-mid)', marginTop: 4 }}>The final day of holiday — not the day they return to work.</p>
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <textarea className="form-control" rows={2} value={logForm.notes} onChange={e => setLogForm({ ...logForm, notes: e.target.value })} placeholder="e.g. Taken before the portal went live" />
              </div>
              <p style={{ fontSize: 12, color: 'var(--grey-mid)' }}>Days are counted automatically (weekends and bank holidays excluded) and deducted from their allowance. No email is sent — this is record-keeping.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setLogModal(false)}>Cancel</button>
              <button className="btn btn-gold" onClick={saveLog} disabled={logSaving}>{logSaving ? 'Saving…' : 'Log Absence'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Amend modal */}
      {amendModal && (
        <div className="modal-overlay" onClick={() => setAmendModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Amend Booking — {amendModal.staff_name}</h3>
              <button className="modal-close" onClick={() => setAmendModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-control" value={amendForm.absence_type} onChange={e => setAmendForm({ ...amendForm, absence_type: e.target.value })}>
                  <option value="holiday">Holiday</option>
                  <option value="sick">Sick Day</option>
                  <option value="awol">Absent Without Leave</option>
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={amendForm.half_day} onChange={e => setAmendForm({ ...amendForm, half_day: e.target.checked })} style={{ width: 16, height: 16, accentColor: 'var(--gold)' }} />
                  <span style={{ fontSize: 14, color: 'var(--navy)' }}>Half day</span>
                </label>
              </div>
              {amendForm.half_day ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label className="form-label">Date</label>
                    <input type="date" className="form-control" value={amendForm.start_date} onChange={e => setAmendForm({ ...amendForm, start_date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">AM or PM</label>
                    <select className="form-control" value={amendForm.half_day_period} onChange={e => setAmendForm({ ...amendForm, half_day_period: e.target.value })}>
                      <option value="am">Morning (AM)</option>
                      <option value="pm">Afternoon (PM)</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label className="form-label">Start Date</label>
                    <input type="date" className="form-control" value={amendForm.start_date} onChange={e => setAmendForm({ ...amendForm, start_date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Day Off</label>
                    <input type="date" className="form-control" value={amendForm.end_date} onChange={e => setAmendForm({ ...amendForm, end_date: e.target.value })} />
                    <p style={{ fontSize: 11, color: 'var(--grey-mid)', marginTop: 4 }}>The final day of holiday — not the day they return to work.</p>
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={amendForm.notes} onChange={e => setAmendForm({ ...amendForm, notes: e.target.value })} />
              </div>
              <p style={{ fontSize: 12, color: 'var(--grey-mid)' }}>Days are recalculated automatically (weekends and bank holidays excluded). The staff member is emailed the corrected booking.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setAmendModal(null)}>Cancel</button>
              <button className="btn btn-gold" onClick={saveAmend} disabled={amendSaving}>{amendSaving ? 'Saving…' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {declineModal && (
        <div className="modal-overlay" onClick={() => setDeclineModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Decline Request</h3>
              <button className="modal-close" onClick={() => setDeclineModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--grey-mid)', fontSize: '13px', marginBottom: '16px' }}>
                Please provide a reason. This will be included in the email notification to the staff member.
              </p>
              <div className="form-group">
                <label className="form-label">Reason for declining</label>
                <textarea className="form-control" value={declineReason} onChange={e => setDeclineReason(e.target.value)} placeholder="e.g. Insufficient cover in the depot during that period..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDeclineModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDecline}>Confirm Decline</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageRequests;
